import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button, Card, Text, tokens } from '@fluentui/react-components';
import i18n from '../../i18n';
import { reportClientError } from '../../telemetry/reportClientError';

interface RouteErrorBoundaryProps {
  children: ReactNode;
}

interface RouteErrorBoundaryState {
  hasError: boolean;
}

/**
 * Catches render/lifecycle errors from the cold-route Suspense tree — most notably a
 * persistent chunk-load failure surfaced by `lazyWithRetry` (the rethrow path taken
 * when a reload has already been attempted once this session). Without this boundary
 * that throw unmounts the whole React root (white screen); with it we show a small
 * recoverable card instead.
 */
export class RouteErrorBoundary extends Component<RouteErrorBoundaryProps, RouteErrorBoundaryState> {
  state: RouteErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): RouteErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('RouteErrorBoundary caught an error', error, errorInfo);
    // Fills the recent-errors ring the feedback dialog attaches (fleet feedback contract v1). Never throws;
    // with VITE_CLIENT_ERROR_ENDPOINT unset nothing leaves the browser.
    reportClientError(error, { componentStack: errorInfo.componentStack ?? undefined, source: 'RouteErrorBoundary' });
  }

  handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: 48,
          }}
        >
          <Card style={{ padding: 32, textAlign: 'center', maxWidth: 360 }}>
            <Text block style={{ marginBottom: 16, color: tokens.colorNeutralForeground2 }}>
              {i18n.t('shell.pageFailed')}
            </Text>
            <Button appearance="primary" onClick={this.handleReload}>
              {i18n.t('shell.reload')}
            </Button>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}
